import React from "react";
import {
  IconArrowRight,
  IconBriefcase,
  IconMapPin,
  IconWorld,
} from "@tabler/icons-react";
import SectionCard from "./SectionCard";
import ServiceCard from "./ServiceCard";
import type { ICountry } from "../../store/@types/locations";
import type { IService } from "../../store/@types/service";
import type { ComplianceService } from "../../store/@types/portal";
import SelectInput from "../elements/form/SelectInput";
import Button from "../elements/form/Button";

type ICountryServicesStep = {
  countryId: string;
  countries: ICountry[];
  services: IService[];
  countriesLoading: boolean;
  servicesLoading: boolean;
  selectedServiceIds: string[];
  onCountryChange: (id: string) => void;
  onToggleService: (serviceId: string) => void;
  onContinue: () => void;
};

const CountryServicesStep: React.FC<ICountryServicesStep> = ({
  countryId,
  countries,
  services,
  countriesLoading,
  servicesLoading,
  selectedServiceIds,
  onCountryChange,
  onToggleService,
  onContinue,
}) => {
  const selectedCountry = countries.find(
    (country) => String(country.id) === countryId,
  );
  const countryServices: ComplianceService[] = services
    .filter((service) => String(service.country_id) === countryId)
    .map((service) => ({
      id: String(service.id),
      name: service.name,
      description: service.description,
      icon: IconBriefcase,
    }));

  return (
    <SectionCard
      icon={<IconWorld size={20} stroke={1.75} />}
      title="Select Country & Services"
    >
      <div className="space-y-6">
        <SelectInput
          label="Country of Interest"
          name="country"
          status=""
          loading={countriesLoading}
          items={countries.map((country) => ({
            id: String(country.id),
            name: country.name,
          }))}
          value={countryId}
          onChange={(e) => onCountryChange(e.target.value)}
        />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900">
              Services Required
            </span>
            {selectedCountry && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50/10 px-3 py-1 text-sm font-semibold text-primary-600">
                <IconMapPin size={13} stroke={2} />
                {selectedCountry.name}
              </span>
            )}
          </div>

          {countryId ? (
            servicesLoading ? (
              <div
                className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500"
                role="status"
              >
                Loading available services...
              </div>
            ) : countryServices.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {countryServices.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    selected={selectedServiceIds.includes(service.id)}
                    onToggle={onToggleService}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
                No services are currently available for this country.
              </div>
            )
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
              Choose a country above to see the services available there.
            </div>
          )}
        </div>

        <Button
          title="Continue to Company Details"
          type="button"
          onHandler={onContinue}
          disabled={
            !countryId ||
            selectedServiceIds.length === 0 ||
            servicesLoading ||
            countriesLoading
          }
          rightIcon={<IconArrowRight size={18} />}
          bg_color="bg-primary-500"
          bg_hoverColor="bg-primary-600"
          className="w-full justify-center rounded-full sm:w-auto"
        />
      </div>
    </SectionCard>
  );
};

export default CountryServicesStep;
